import {
  cupsForGlobalProgress,
  loopSpeedRamp,
  starsForLoop1Progress,
  type LoopSpeedRamp,
  type MilestoneKind,
} from '@shared/progression';

/**
 * Per-run loop bookkeeping. The {@link GameSession} owns one of these
 * for the duration of a play attempt; it tracks which loop is active,
 * which star/cup milestones have already fired, and exposes helpers
 * for milestone detection so {@link GameSession.tick} can stay focused
 * on engine + FX orchestration.
 *
 * Score / combo / verdict counters live in the engine state and
 * accumulate across loops naturally (we never zero them between
 * loops). Only the chart-relative parts of the engine state reset
 * (beat, hitPadIds, alive/completed flags, hp) — see
 * `resetEngineStateForNextLoop` in `@shared/engine/pulse/state`.
 *
 * The loop count is unbounded — once the player has cleared the song
 * once they keep going until they die, with each repeat stepping the
 * playback rate up by {@link loopSpeedRamp}. Cup milestones still cap
 * at 3 (the last earnable trophy fires on loop 3 completion); every
 * loop past that is pure score accumulation at higher speed.
 */
export class LoopRunState {
  /** 0-based: 0 = first play, 1+ = repeats (each loop runs at a higher speed). */
  private loopIndex = 0;
  /**
   * Milestones already fired this run. Each kind fires at most once,
   * so re-entering an already-passed beat range (e.g. lap of an
   * unusual chart) doesn't double-celebrate.
   */
  private firedMilestones: Set<MilestoneKind> = new Set();

  constructor(private readonly totalBeats: number) {}

  getLoopIndex(): number {
    return this.loopIndex;
  }

  /**
   * Flat speed multiplier for the current loop. Loop 0 plays at native
   * tempo; each subsequent repeat steps up by a constant increment.
   */
  getCurrentSpeedRamp(): LoopSpeedRamp {
    return loopSpeedRamp(this.loopIndex);
  }

  hasFired(kind: MilestoneKind): boolean {
    return this.firedMilestones.has(kind);
  }

  /**
   * Mark a milestone as fired so we don't re-celebrate. Returns true
   * if this was the first firing (caller should play FX), false if
   * it was already in the set.
   */
  markFired(kind: MilestoneKind): boolean {
    if (this.firedMilestones.has(kind)) return false;
    this.firedMilestones.add(kind);
    return true;
  }

  /** Total stars earned this run (across the loop-1 thresholds). */
  countStars(): number {
    let n = 0;
    if (this.firedMilestones.has('star_1')) n += 1;
    if (this.firedMilestones.has('star_2')) n += 1;
    if (this.firedMilestones.has('star_3')) n += 1;
    return n;
  }

  /** Total cups earned this run. */
  countCups(): number {
    let n = 0;
    if (this.firedMilestones.has('cup_1')) n += 1;
    if (this.firedMilestones.has('cup_2')) n += 1;
    if (this.firedMilestones.has('cup_3')) n += 1;
    return n;
  }

  /**
   * Detect star/cup milestones we should fire this tick based on the
   * current loop and the engine's beat. Returns an in-order list (so
   * a frame that crosses both a 1/3 and a 2/3 threshold fires star_1
   * then star_2). The caller is responsible for calling
   * {@link markFired} on each.
   */
  detectMidRunMilestones(beat: number): MilestoneKind[] {
    const out: MilestoneKind[] = [];
    if (this.loopIndex === 0) {
      // Loop 1 — earn stars at 1/3 and 2/3 progress. star_3 is fired
      // at the engine `completed` event (handled separately).
      const stars = starsForLoop1Progress(beat / this.totalBeats);
      if (stars >= 1 && !this.hasFired('star_1')) out.push('star_1');
      if (stars >= 2 && !this.hasFired('star_2')) out.push('star_2');
    } else if (this.loopIndex === 1) {
      // Loop 2 — first cup at 1.5 songs total = halfway through loop 2.
      const globalProgress = 1 + beat / this.totalBeats;
      const cups = cupsForGlobalProgress(globalProgress);
      if (cups >= 1 && !this.hasFired('cup_1')) out.push('cup_1');
    }
    // Loops 3+ have no mid-run milestones (cup_3 fires on the loop-3
    // completion event; loops past that are pure score accumulation).
    return out;
  }

  /**
   * Pick the milestone that fires when the engine emits `completed`
   * for the current loop. The final earnable trophy is `cup_3` at the
   * end of loop 3; subsequent loops in infinite mode have no
   * milestones to fire and return `null`.
   */
  detectCompletionMilestone(): MilestoneKind | null {
    switch (this.loopIndex) {
      case 0:
        return this.hasFired('star_3') ? null : 'star_3';
      case 1:
        return this.hasFired('cup_2') ? null : 'cup_2';
      case 2:
        return this.hasFired('cup_3') ? null : 'cup_3';
      default:
        return null;
    }
  }

  /** Advance to the next loop. Returns the new loop index. */
  advanceLoop(): number {
    this.loopIndex += 1;
    return this.loopIndex;
  }
}
